import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { Box, Chip, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { masterApi } from "../../apis/masterApi";
import { encodeData } from "../../helper/common/encodeData";
import {
  buildFiltersArray,
  createFilterRow,
  serializeFilterRowValue,
} from "../../helper/common/buildSearchFilters";
import { formatDateFn } from "../../helper/common/formatDateForAPI";
import { getTodayCalendarDate } from "../../constant/calendarDefaultConfigurations";
import Header from "../../components/header/header";
import MultiFilterSearch, {
  AppliedFilterChip,
} from "../../components/MultiFilterSearch/MultiFilterSearch";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import RequestTrackingTable from "./RequestTrackingTable";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];
const DEFAULT_SEARCH_TYPE = "task_type";
const IN_PROGRESS_POLL_INTERVAL_MS = 5000;
const POLLABLE_STATUSES = ["PICKED", "IN_PROGRESS"];

const TASK_TYPE_OPTIONS = [
  { label: "Bulk ASIN Status Enable", value: "BULK_ASIN_STATUS_ENABLE" },
  { label: "Bulk ASIN Status Pause", value: "BULK_ASIN_STATUS_PAUSE" },
  { label: "Bulk ASIN Populate", value: "BULK_ASIN_POPULATE" },
  { label: "Bulk Campaign Update", value: "BULK_CAMPAIGN_UPDATE" },
  { label: "Bulk Negative Target Add", value: "BULK_NEGATIVE_TARGET_ADD" },
  { label: "Bulk Placement Update", value: "BULK_PLACEMENT_UPDATE" },
  { label: "Bulk Target Update", value: "BULK_TARGET_UPDATE" },
  { label: "Partial Back Sync", value: "PARTIAL_BACK_SYNC" },
  { label: "Report Export", value: "REPORT_EXPORT" },
];

const STATUS_OPTIONS = [
  { label: "Picked", value: "PICKED" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Success", value: "SUCCESS" },
  { label: "Partial Success", value: "PARTIAL_SUCCESS" },
  { label: "Failure", value: "FAILURE" },
];

const SEARCH_TYPE_OPTIONS = [
  {
    label: "Task Type",
    value: "task_type",
    operator: "IN",
    inputType: "multiSelect",
    placeholder: "Select task type",
    choices: TASK_TYPE_OPTIONS,
  },
  {
    label: "Status",
    value: "status",
    operator: "IN",
    inputType: "multiSelect",
    placeholder: "Select status",
    choices: STATUS_OPTIONS,
  },
  { label: "Triggered By", value: "triggered_by", operator: "CONTAINS", inputType: "text" },
];

const TABLE_COLUMNS = [
  { label: "Request Details", id: "request_details", width: 220, isSticky: true },
  { label: "Activity Time", id: "activity_time", width: 180 },
  { label: "Summary", id: "summary", width: 220 },
  { label: "ASINs", id: "asins", width: 160 },
  { label: "Status", id: "status", width: 160 },
  { label: "Action", id: "action", width: 160 },
  { label: "Triggered By", id: "triggered_by", width: 180 },
];

function parseRequestTrackingResponse(result) {
  const payload = result?.data;
  if (Array.isArray(payload)) {
    return {
      data: payload,
      totalCount: result?.total_count ?? null,
    };
  }
  if (payload && Array.isArray(payload?.data)) {
    return {
      data: payload.data,
      totalCount:
        payload?.total_count ??
        payload?.totalCount ??
        payload?.total ??
        result?.total_count ??
        null,
    };
  }
  return { data: [], totalCount: null };
}

function parseSingleTrackingResponse(result) {
  const payload = result?.data;
  if (payload?.tracking_id) {
    return payload;
  }
  if (payload?.data?.tracking_id) {
    return payload.data;
  }
  return null;
}

function formatDisplayDate(dateString) {
  if (!dateString) return "-";
  return moment(dateString).format("DD MMM hh:mm A");
}

function extractAsins(taskPayload) {
  return (taskPayload?.items ?? [])
    .map((item) => item?.value)
    .filter(Boolean);
}

function extractAsinsFileUrl(taskPayload) {
  return taskPayload?.action?.parameters?.file_url || null;
}

function mapTrackingRow(item) {
  return {
    ...item,
    tracking_id_display: item.tracking_id
      ? `${item.tracking_id.slice(0, 20)}...`
      : "-",
    time_created_display: formatDisplayDate(item.time_created),
    time_last_updated_display: formatDisplayDate(item.time_last_updated),
    time_completed_display: formatDisplayDate(item.time_completed),
    asins: extractAsins(item.task_payload),
    asins_file_url: extractAsinsFileUrl(item.task_payload),
  };
}

const RequestTracking = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const navigate = useNavigate();

  // Page-local calendar only — does not mutate global calendar context
  const [calendarDate, setCalendarDate] = useState(() => getTodayCalendarDate());
  const [applyCalendar, setApplyCalendar] = useState(1);

  const [rawTrackingData, setRawTrackingData] = useState([]);
  const [isLoaderVisible, setIsLoaderVisible] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const [filterRows, setFilterRows] = useState([
    createFilterRow(DEFAULT_SEARCH_TYPE),
  ]);
  const [appliedFilters, setAppliedFilters] = useState([]);

  const isInitialLoadRef = useRef(true);
  const appliedFiltersRef = useRef(appliedFilters);
  const rawTrackingDataRef = useRef(rawTrackingData);
  appliedFiltersRef.current = appliedFilters;
  rawTrackingDataRef.current = rawTrackingData;

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const hasActiveSearch = appliedFilters.length > 0;

  const trackingDataToShow = useMemo(
    () => rawTrackingData.map(mapTrackingRow),
    [rawTrackingData]
  );

  const updateTotalCount = useCallback(
    (filteredLength, apiPage, limit, totalFromApi) => {
      if (totalFromApi !== null && totalFromApi !== undefined) {
        setTotalCount(totalFromApi);
        return;
      }
      if (filteredLength < limit) {
        setTotalCount(apiPage * limit + filteredLength);
        return;
      }
      setTotalCount((prev) => Math.max(prev, (apiPage + 1) * limit + 1));
    },
    []
  );

  const fetchTrackingData = useCallback(
    async ({
      pageNumber = 1,
      isInitial = false,
      activeFilters = appliedFiltersRef.current,
    } = {}) => {
      const apiPage = pageNumber - 1;
      const requestData = {
        source: JSON.parse(localStorage.getItem("user_platform")),
        limit: itemsPerPage,
        page: apiPage,
        start_date: formatDateFn(calendarDate[0].startDate),
        end_date: formatDateFn(calendarDate[0].endDate),
      };

      const filters = buildFiltersArray(activeFilters, SEARCH_TYPE_OPTIONS);

      if (filters.length) {
        requestData.filtering = encodeData(filters);
      }

      if (isInitial) {
        setIsLoaderVisible(true);
      } else {
        setPaginationLoading(true);
      }

      try {
        const result = await masterApi(requestData, "requestTracking", false);
        const { data, totalCount: totalFromApi } =
          parseRequestTrackingResponse(result);
        setRawTrackingData(data);
        updateTotalCount(data.length, apiPage, itemsPerPage, totalFromApi);
        setCurrentPage(pageNumber);
      } catch (error) {
        console.log(error);
      } finally {
        if (isInitial) {
          setIsLoaderVisible(false);
        } else {
          setPaginationLoading(false);
        }
      }
    },
    [calendarDate, itemsPerPage, updateTotalCount]
  );

  const fetchTrackingRowById = useCallback(async (trackingId) => {
    const result = await masterApi(
      {},
      "requestTrackingById",
      false,
      undefined,
      trackingId
    );
    return parseSingleTrackingResponse(result);
  }, []);

  const replaceTrackingRow = useCallback((trackingId, updatedRow) => {
    setRawTrackingData((prev) =>
      prev.map((row) => (row.tracking_id === trackingId ? updatedRow : row))
    );
  }, []);

  useEffect(() => {
    if (isLoaderVisible || paginationLoading) {
      return undefined;
    }

    const pollInProgressRows = async () => {
      const inProgressRows = rawTrackingDataRef.current.filter((row) =>
        POLLABLE_STATUSES.includes(row.status)
      );

      if (!inProgressRows.length) {
        return;
      }

      await Promise.all(
        inProgressRows.map(async (row) => {
          const trackingId = row.tracking_id;
          if (!trackingId) return;

          try {
            const updatedRow = await fetchTrackingRowById(trackingId);
            if (updatedRow?.tracking_id) {
              replaceTrackingRow(trackingId, updatedRow);
            }
          } catch (error) {
            console.log(error);
          }
        })
      );
    };

    const intervalId = setInterval(pollInProgressRows, IN_PROGRESS_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [
    isLoaderVisible,
    paginationLoading,
    fetchTrackingRowById,
    replaceTrackingRow,
  ]);

  const handleLocalCalendarChange = useCallback(
    ({ calendarDate: nextCalendarDate, applyCalendar: nextApplyCalendar }) => {
      if (nextCalendarDate) {
        setCalendarDate(nextCalendarDate);
      }
      if (typeof nextApplyCalendar === "number") {
        setApplyCalendar(nextApplyCalendar);
      }
    },
    []
  );

  useEffect(() => {
    const isInitialLoad = isInitialLoadRef.current;
    isInitialLoadRef.current = false;
    fetchTrackingData({ pageNumber: 1, isInitial: isInitialLoad });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, applyCalendar, itemsPerPage]);

  const handlePageChange = (pageNumber) => {
    fetchTrackingData({ pageNumber, isInitial: false });
  };

  const handlePageLimitChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    const nextAppliedFilters = filterRows
      .map((row) => ({
        searchType: row.searchType,
        searchText: serializeFilterRowValue(row, SEARCH_TYPE_OPTIONS),
      }))
      .filter((row) => row.searchText);
    setAppliedFilters(nextAppliedFilters);
  };

  const handleClearAllFilters = () => {
    setFilterRows([createFilterRow(DEFAULT_SEARCH_TYPE)]);
    setAppliedFilters([]);
  };

  const handleRemoveAppliedFilter = (index) => {
    const nextApplied = appliedFilters.filter((_, itemIndex) => itemIndex !== index);
    setAppliedFilters(nextApplied);
    setFilterRows(
      nextApplied.length
        ? nextApplied.map((filter) =>
            createFilterRow(filter.searchType, filter.searchText)
          )
        : [createFilterRow(DEFAULT_SEARCH_TYPE)]
    );
  };

  const handleViewHistoryLogs = (trackingId) => {
    navigate(`/history-logs?tracking_id=${encodeURIComponent(trackingId)}`);
  };

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box sx={{ px: 0, pb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Request Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Monitor bulk ASIN status update requests and their progress.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexGrow: 1,
              maxWidth: "calc(100% - 180px)",
            }}
          >
            <MultiFilterSearch
              searchTypeOptions={SEARCH_TYPE_OPTIONS}
              defaultSearchType={DEFAULT_SEARCH_TYPE}
              filterRows={filterRows}
              onFilterRowsChange={setFilterRows}
              onApply={handleApplyFilters}
              onCreateRow={createFilterRow}
            />
          </Box>

          <Header
            showOnlyCalendar={true}
            includeToday={true}
            localCalendar={true}
            onLocalCalendarChange={handleLocalCalendarChange}
          />
        </Box>

        {hasActiveSearch && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
            {appliedFilters.map((filter, index) => (
              <AppliedFilterChip
                key={`${filter.searchType}-${filter.searchText}-${index}`}
                filter={filter}
                searchTypeOptions={SEARCH_TYPE_OPTIONS}
                onDelete={() => handleRemoveAppliedFilter(index)}
              />
            ))}
            {appliedFilters.length > 1 && (
              <Chip
                size="small"
                variant="outlined"
                label="Clear all"
                onClick={handleClearAllFilters}
              />
            )}
          </Box>
        )}

        <RequestTrackingTable
          columns={TABLE_COLUMNS}
          rows={trackingDataToShow}
          isLoading={isLoaderVisible || paginationLoading}
          onViewHistoryLogs={handleViewHistoryLogs}
        />

        {!isLoaderVisible &&
          !paginationLoading &&
          (rawTrackingData?.length > 0 || totalCount > 0) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                py: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#4f5366",
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "12px" }}>
                  Page Size
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={itemsPerPage}
                    onChange={handlePageLimitChange}
                    disabled={paginationLoading}
                    sx={{
                      height: 36,
                      fontSize: "12px",
                      color: "#333333",
                    }}
                    MenuProps={{ disableScrollLock: true }}
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{ fontSize: "12px" }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                lazyLoading={paginationLoading}
                totalItems={totalCount}
                itemsPerPage={itemsPerPage}
              />
            </Box>
          )}
      </Box>
    </div>
  );
};

export default RequestTracking;
